import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Rating,
  TextField,
  Alert,
  Chip,
  Stack,
  Snackbar,
} from "@mui/material";
import { Star, Close, ListAlt } from "@mui/icons-material";
import { FinishPageProps } from "../../Interfaces/ComponentProps";
import { useSubmitRatingMutation } from "../../Apis/progressApi";

const FinishPage: React.FC<FinishPageProps> = ({
  results,
  username,
  progressId,
  onViewScoreboard,
  onClose,
}) => {
  const [submitRating] = useSubmitRatingMutation();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const currentScore = results?.finalScore || 0;
  const currentBaseScore = results?.score || 0;

  const [ratingValue, setRatingValue] = useState<number | null>(5);
  const [comment, setComment] = useState("");
  const [ratingLoading, setRatingLoading] = useState(false);
  const [ratingSuccess, setRatingSuccess] = useState<string | null>(null);
  const [ratingError, setRatingError] = useState<string | null>(null);

  if (!results) return null;

  const percentage: number = Math.round(
    (currentBaseScore / (results.totalQuestions * 10)) * 100
  );
  const roundedTimeFactor = Math.round(results.timeFactor * 100) - 100;
  const roundedFinalScore = Math.round(currentScore);
  const correctAnswers = Math.round(currentBaseScore / 10);

  const formatTimeString = (timeString: string) => {
    try {
      const [hours, minutes, seconds] = timeString.split(":");
      const cleanSeconds = seconds.split(".")[0];
      const totalMinutes = parseInt(hours) * 60 + parseInt(minutes);
      return `${totalMinutes}:${cleanSeconds.padStart(2, "0")}`;
    } catch {
      return timeString;
    }
  };

  const formattedTime = formatTimeString(results.timeTaken);

  const getScoreColor = (): "success" | "warning" | "error" => {
    if (percentage >= 80) return "success";
    if (percentage >= 60) return "warning";
    return "error";
  };

  const getScoreMessage = (): string => {
    if (percentage >= 90) return "Excellent! You are a quiz master!";
    if (percentage >= 80) return "Great job! You know your stuff!";
    if (percentage >= 70) return "Good effort! Keep learning!";
    if (percentage >= 60)
      return "Not bad! Try again next quiz for a better score!";
    return "Keep trying! You can do better next time!";
  };

  const showSnackbar = (msg: string) => {
    setSnackbarMessage(msg);
    setSnackbarOpen(true);
  };

  const handleShare = async () => {
    if (!progressId) return;

    const shareLink = `${window.location.origin}/Register?ref=${progressId}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: "Try this quiz!",
          text: "Give it a try and see how you do!",
          url: shareLink,
        });

        showSnackbar(
          "Share your link! Earn 20 bonus points when someone registers using it."
        );
      } catch {
        try {
          await navigator.clipboard.writeText(shareLink);
          showSnackbar("Share dialog closed. Link copied to clipboard!");
        } catch {
          showSnackbar("Copy this link manually: " + shareLink);
        }
      }
    } else {
      try {
        await navigator.clipboard.writeText(shareLink);
        showSnackbar("Link copied to clipboard!");
      } catch {
        showSnackbar("Copy this link manually: " + shareLink);
      }
    }
  };

  const handleSubmitRating = async () => {
    setRatingSuccess(null);
    setRatingError(null);

    if (!progressId) return setRatingError("Progress ID missing.");
    if (!ratingValue || ratingValue < 1)
      return setRatingError("Please select a rating.");

    setRatingLoading(true);
    try {
      const payload = {
        progressId: Number(progressId),
        rate: Number(ratingValue),
        comment: comment.trim() || "",
      };
      const response = await submitRating(payload).unwrap();
      setRatingSuccess(response.message ?? "Rating saved");
      setComment("");
    } catch (err: any) {
      setRatingError(
        err?.data?.message || err?.message || "Failed to submit rating."
      );
    } finally {
      setRatingLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8f9fa", p: 0 }}>
      {/* Header */}
      <Box
        sx={{
          bgcolor: "success.main",
          color: "white",
          py: 2,
          px: 3,
          borderRadius: 1,
          mb: 3,
        }}
      >
        <Typography variant="h5">Contest Completed!</Typography>
        <Typography>Congratulations, {username}!</Typography>
      </Box>

      {/* Results Card */}
      <Card sx={{ maxWidth: 800, mx: "auto", p: 1, mb: 3 }}>
        <CardContent sx={{ textAlign: "center" }}>
          {/* Celebration */}
          <Box
            sx={{
              width: 100,
              height: 100,
              borderRadius: "50%",
              bgcolor: "success.light",
              color: "success.main",
              mx: "auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 3,
              fontSize: 48,
            }}
          >
            <Star fontSize="inherit" />
          </Box>

          {/* Score Display */}
          <Typography variant="h4" gutterBottom>
            {currentBaseScore}/{results.totalQuestions * 10}
          </Typography>
          <Chip
            label={`${percentage}%`}
            color={getScoreColor()}
            sx={{ mb: 2 }}
          />
          <Typography>
            You answered {correctAnswers} out of {results.totalQuestions}{" "}
            questions correctly in <strong>{formattedTime}</strong>
          </Typography>
          <Typography
            component="span"
            sx={{ display: "inline-flex", alignItems: "center" }}
          >
            Time bonus: <strong>{roundedTimeFactor}%</strong>
          </Typography>
          <Typography variant="h4" color="primary" sx={{ mt: 2 }}>
            Total Score: {roundedFinalScore}
          </Typography>

          {/* Score Message */}
          <Alert severity={getScoreColor()} sx={{ mt: 3 }}>
            <Typography variant="h6">{getScoreMessage()}</Typography>
          </Alert>

          {/* Rating */}
          {progressId && !ratingSuccess && (
            <Card sx={{ mt: 3, p: 2 }}>
              {ratingSuccess && (
                <Alert severity="success">{ratingSuccess}</Alert>
              )}
              {ratingError && <Alert severity="error">{ratingError}</Alert>}
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, mb: 1, color: "primary.main" }}
              >
                Rate this quiz
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Please rate the quiz and optionally leave a comment.
              </Typography>
              <Rating
                size="large"
                value={ratingValue}
                onChange={(_, newValue) => setRatingValue(newValue)}
              />
              <TextField
                fullWidth
                multiline
                minRows={2}
                placeholder="Comment (optional)"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                sx={{ mt: 2 }}
              />
              <Button
                variant="contained"
                color="success"
                onClick={handleSubmitRating}
                disabled={ratingLoading}
                sx={{ mt: 2 }}
              >
                {ratingLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Submit"
                )}
              </Button>
            </Card>
          )}

          {/* Share / Actions */}
          <Stack
            direction={{ xs: "column", sm: "row" }} // vertical on xs, horizontal on sm+
            spacing={2} // spacing between items
            justifyContent="center"
            alignItems="center"
            sx={{ mt: 3 }}
          >
            <Button
              variant="contained"
              color="primary"
              sx={{ m: 3 }}
              onClick={handleShare}
            >
              Share & Earn 20 Points
            </Button>
            <Button
              sx={{ m: 3 }}
              variant="contained"
              color="info"
              onClick={onViewScoreboard}
              startIcon={<ListAlt />}
            >
              View Scoreboard
            </Button>
            <Button
              sx={{ m: 3 }}
              variant="outlined"
              color="secondary"
              onClick={() => window.location.reload()}
              startIcon={<Close />}
            >
              Close
            </Button>
          </Stack>

          <Typography variant="caption" display="block" sx={{ mt: 3 }}>
            Share this contest to earn bonus points when someone registers using your link.
          </Typography>
        </CardContent>
      </Card>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="info"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FinishPage;
